import type { BaseEntity, GraphqlOperation } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import type { RequestIdManager } from '@funtech-int/yango-shared';
import { createRef, Instance } from '@funtech-int/rival';
import { createFetcher, FetcherMiddleware } from '@funtech-int/web-toolkit';
import { GraphQLError, type ASTNode, type Source } from 'graphql';

import { RequestIdsHeader } from '@kinopoisk-int/request-ids';
import type { Logger } from '@kinopoisk-int/logger';

export const graphqlClientRef = createRef<GraphqlClient>('GraphqlClient');

export interface GraphqlResponse<Entity extends BaseEntity<unknown> | null> {
    data: Entity | null | undefined;
    errors?: GraphQLError[];
    requestId: string;
}

interface RequesterConfig {
    headers?: Record<string, string>;
    abortSignal?: AbortSignal;
}

export interface GraphqlClient {
    fetch<Query, Entity extends BaseEntity<unknown> | null, Variables extends object>(
        { operation, variables }: FetchParams<Query, Entity, Variables>,
        options?: RequesterConfig
    ): Promise<GraphqlResponse<Entity>>;
}

interface FetchParams<Query, Entity extends BaseEntity<unknown> | null, Variables extends object> {
    operation: GraphqlOperation<Query, any, Entity, Variables>;
    variables?: Variables;
}
// ts-prune-ignore-next-line
export function transformUnknownErrorToGraphqlError(error: unknown): GraphQLError | undefined {
    if (!(error && typeof error === 'object')) {
        return undefined;
    }
    if (!('message' in error && typeof error.message === 'string')) {
        return undefined;
    }
    return new GraphQLError(
        error.message,
        'nodes' in error ? (error.nodes as ASTNode) : undefined,
        'source' in error ? (error.source as Source) : undefined,
        'positions' in error ? (error.positions as number[]) : undefined,
        'path' in error ? (error.path as string[]) : undefined,
        'originalError' in error ? (error.originalError as Error) : undefined,
        'extensions' in error ? (error.extensions as Record<string, unknown>) : undefined
    );
}

interface GraphqlBaseResponse<DataType> {
    errors?: Array<unknown>;
    data: DataType | null;
}

interface CreateGraphqlClientParams {
    requestIdManager: RequestIdManager;
    logger: Logger;
    lang: string;
    serviceId: number;
    timeout: number;
    graphqlBaseUrl: string;
    defaultHeaders?: Record<string, string>;
    middlewares?: FetcherMiddleware[];
}

const loggerErrorType = 'graphql-error';

function getGraphqlErrorMessage({
    graphqlError,
    operationName,
}: {
    graphqlError: GraphQLError;
    operationName: string;
}) {
    return [
        loggerErrorType,
        operationName,
        graphqlError.extensions?.type ?? graphqlError.extensions?.classification,
        graphqlError.extensions?.reason,
    ]
        .filter(Boolean)
        .join(' ');
}

function getGraphqlErrorContext({
    graphqlError,
    operationName,
    requestTime,
}: {
    graphqlError: GraphQLError;
    operationName: string;
    requestTime: number | undefined;
}) {
    const { message, path, extensions } = graphqlError;
    return {
        errorMessage: message,
        errorPath: path?.join('.'),
        errorType: extensions?.type,
        errorClassification: extensions?.classification,
        errorReason: extensions?.reason,
        operationName,
        requestTime,
    };
}

function logGraphqlError({
    logger,
    graphqlError,
    operationName,
    requestTime,
}: {
    logger: Logger | Console;
    graphqlError: GraphQLError;
    operationName: string;
    requestTime: number | undefined;
}) {
    logger.error(
        getGraphqlErrorContext({ graphqlError, operationName, requestTime }),
        getGraphqlErrorMessage({ graphqlError, operationName })
    );
}

async function parseGraphqlJsonResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        throw new Error([response.status, response.statusText].filter(Boolean).join(' '));
    }
    const text = await response.text();
    return (text.length > 0 ? JSON.parse(text) : null) as T;
}

export function createGraphqlClient({
    requestIdManager,
    logger,
    lang,
    serviceId,
    timeout,
    graphqlBaseUrl,
    defaultHeaders = {},
    middlewares = [],
}: CreateGraphqlClientParams): Instance<GraphqlClient> {
    const baseUrl = graphqlBaseUrl;

    const fetcher = createFetcher(
        {
            baseUrl,
            timeout,
            credentials: 'include',
        },
        middlewares
    );

    return {
        async fetch({ operation, variables }, config?: RequesterConfig) {
            const requestId = requestIdManager.requestId.child;
            const headers: Record<string, string> = {
                ...defaultHeaders,
                'service-id': String(serviceId),
                'content-type': 'application/json',
                [RequestIdsHeader.RequestId]: requestId,
                [RequestIdsHeader.Traceparent]: requestIdManager.traceparent,
                'x-preferred-language': lang,
                'accept-language': lang,
                ...config?.headers,
            };

            const postData = JSON.stringify({
                operationName: operation.name,
                variables,
                query: operation.body,
            });
            const url = `?operationName=${operation.name}`;
            const startTime = Date.now();

            const response = await fetcher.fetch(url, {
                method: 'POST',
                headers,
                body: postData,
                signal: config?.abortSignal,
            });

            const { data, errors: errorsDto } = await parseGraphqlJsonResponse<
                GraphqlBaseResponse<unknown>
            >(response);
            const requestTime = Date.now() - startTime;

            const errors = errorsDto
                ? errorsDto
                      .map(transformUnknownErrorToGraphqlError)
                      .filter((err): err is GraphQLError => Boolean(err))
                : undefined;

            if (errors) {
                errors.forEach((graphqlError) =>
                    logGraphqlError({
                        logger,
                        operationName: operation.name,
                        graphqlError,
                        requestTime,
                    })
                );
            }

            return {
                data: operation.hydrate(
                    operation.resolve(
                        data as Parameters<typeof operation.resolve>[0],
                        errors,
                        variables
                    )
                ),
                errors,
                requestId,
            };
        },

        ref() {
            return graphqlClientRef;
        },
    };
}
